import axios from "../config/axios-chatbot";

// WebRTC agent calling (SIP over WSS through Asterisk), see sip-connector/README_WEBRTC.md "Client contract".
// Every call is scoped to a registry project: /api/project/{projectId}/webrtc/..., with X-Project-Id equal to the
// path id. Errors are {code, error, ...}: branch on webrtcErrorCode.
const base = (projectId) => `/api/project/${projectId}/webrtc`;
const config = (projectId, extra) => ({headers: {"X-Project-Id": projectId}, ...(extra || {})});

// Error code of a failed call ("agent_not_registered", "conversation_not_active", ...), null when there is none
export const webrtcErrorCode = (error) => error?.response?.data?.code || null;

// Message to show for a failed call
export const webrtcErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

// An agent group's live count (online_count, waiting_count, ...): "–" while Asterisk is unreachable (null)
export const groupCount = (value) => (value === null || value === undefined ? "–" : value);

export const WebrtcRepository = {
  // -> {agent: {id, sip_id, registered, device_state, ...}, webrtc: {sip_uri, authorization_user, password, ws_url,
  // display_name, ice_servers, ice_credentials_expire_in}}. Creates the agent's WebRTC identity on first use; call it
  // on every connect, it is not cached. The identity is per project: one registration calls for every sub project.
  getMyWebrtc: ({projectId, displayName}) => axios.post(`${base(projectId)}/agent`, {
    display_name: displayName || undefined,
  }, config(projectId)),
  // Deletes a project member's WebRTC agent (SIP password, agent groups) -> {deleted, agents}. ADMIN / MANAGER.
  // The agent is keyed by the user's email, else username: both are tried. timeout in ms.
  deleteMemberAgent: ({projectId, username, email, timeout}) => axios.delete(`${base(projectId)}/agents`, config(projectId, {
    params: {username: username || undefined, email: email || undefined},
    timeout,
  })),
  // Agent groups: a project-level number that rings every joined agent's browser at once; unanswered calls (or
  // nobody online) go to its transfer_number. AgentGroup: {id, name, number, transfer_number, sub_project_id,
  // wait_timeout, ring_timeout, members_count, online_count, available_count, waiting_count, longest_wait_seconds};
  // the live counts are null when Asterisk is unreachable.
  // -> {groups: AgentGroup[]}. ADMIN / MANAGER / AGENT
  getGroups: ({projectId}) => axios.get(`${base(projectId)}/groups`, config(projectId)),
  // -> AgentGroup + members[{display_name, state, online, available}] + waiting[{position, wait_seconds, caller_id,
  // conversation_id}]
  getGroup: ({projectId, groupId}) => axios.get(`${base(projectId)}/groups/${groupId}`, config(projectId)),
  // group: {name, number?, transfer_number?, sub_project_id?, wait_timeout? (5-3600 s), ring_timeout? (5-300 s)}.
  // An empty number is assigned automatically (7000000+). Errors: 400 name_required, 409 number_taken. ADMIN / MANAGER
  createGroup: ({projectId, group}) => axios.post(`${base(projectId)}/groups`, group, config(projectId)),
  editGroup: ({projectId, groupId, group}) => axios.patch(`${base(projectId)}/groups/${groupId}`, group, config(projectId)),
  deleteGroup: ({projectId, groupId}) => axios.delete(`${base(projectId)}/groups/${groupId}`, config(projectId)),
  // -> {groups: (AgentGroup + joined)[]}: every group of the project with the logged-in agent's membership
  getMyGroups: ({projectId}) => axios.get(`${base(projectId)}/agent/groups`, config(projectId)),
  // -> {joined, changed}; idempotent. 409 agent_not_registered before the agent's first Connect
  joinGroup: ({projectId, groupId}) => axios.post(`${base(projectId)}/groups/${groupId}/join`, {}, config(projectId)),
  leaveGroup: ({projectId, groupId}) => axios.post(`${base(projectId)}/groups/${groupId}/leave`, {}, config(projectId)),
  // a live conversation to a group (works with nobody online: the call goes to its transfer_number).
  // -> {conversation_id, group_id, status: "HANDOVER", transfer}. Errors: 404 conversation_not_active
  handoverToGroup: ({projectId, conversationId, groupId}) =>
    axios.post(`${base(projectId)}/conversations/${conversationId}/handover-group`, {group_id: groupId}, config(projectId)),
  // -> {external_agent_id, provisioned, registered, agent?}
  getWebrtcStatus: ({projectId}) => axios.get(`${base(projectId)}/agent/status`, config(projectId)),
  // -> {conversation_id, external_agent_id, status: "HANDOVER", transfer}: the caller is bridged to the agent's browser.
  // Errors: 404 conversation_not_active, 409 agent_not_registered, 503 ami_unavailable
  handoverToWebrtc: ({projectId, conversationId}) =>
    axios.post(`${base(projectId)}/conversations/${conversationId}/handover`, {}, config(projectId)),
  // {conversationId} or {phoneNumber} -> {action_id, external_agent_id, to_number, caller_id}. The agent's browser
  // rings first ("Call <number>"), then the customer is dialed from the sub project's outbound_call (caller ID and
  // trunk). sub_project_id defaults to the conversation's own.
  // Errors: 400 to_number_required / invalid_phone_number, 404 conversation_not_found,
  // 422 outbound_number_not_configured, 409 agent_not_registered, 503 ami_unavailable
  webrtcCallCustomer: ({projectId, subProjectId, conversationId, phoneNumber}) => axios.post(`${base(projectId)}/calls`, {
    conversation_id: conversationId ?? undefined,
    to_number: conversationId ? undefined : phoneNumber,
    sub_project_id: subProjectId || undefined,
  }, config(projectId)),
  // -> {status: "pending" | "done", outcome: answered | rejected | busy | no_answer | invalid_number | network_error |
  // hangup | congestion | failed | unknown}, of the agent's leg (the customer is dialed once the agent answers)
  pollCallStatus: ({projectId, actionId}) => axios.get(`${base(projectId)}/calls/${actionId}`, config(projectId)),
};
